import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  ShoppingCart,
  Package, 
  Pill,
  Sparkles,
  ShoppingBag,
  Gift
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LoyaltyCard } from '../components/loyalty';
import { CureSetupModal } from '../components/cure';
import {
  GlobalSearchSection,
  CosmeticsTab,
  SupplementsTab,
  SpaTab,
  ConsumablesTab,
  CartDialog,
  SaleConfirmationDialog,
  BottomSaleBar,
  productMatchesConcern
} from '../components/products';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductsPage = ({ onBack, diagnosis, onSaleComplete }) => {
  const { language } = useLanguage();
  
  // Data states
  const [products, setProducts] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [recommendedSupplements, setRecommendedSupplements] = useState([]);
  const [allRecommendations, setAllRecommendations] = useState({
    cosmetics: [], complements: [], spa: [], consumables: []
  });
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedConcern, setSelectedConcern] = useState('all');
  const [supplementSearch, setSupplementSearch] = useState('');
  const [selectedSupplementCategory, setSelectedSupplementCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('cosmetics');
  
  // Cart states
  const [cart, setCart] = useState([]);
  const [supplementCart, setSupplementCart] = useState([]);
  const [consumablesCart, setConsumablesCart] = useState([]);
  
  // UI states
  const [showCart, setShowCart] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showLoyalty, setShowLoyalty] = useState(false);
  const [showCureSetup, setShowCureSetup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saleReceipt, setSaleReceipt] = useState(null);
  const [saleCompleted, setSaleCompleted] = useState(false);
  const [cureSetupData, setCureSetupData] = useState(null);
  
  // Sale data
  const [ticketMediciel, setTicketMediciel] = useState('');
  const [scheduleRenewals, setScheduleRenewals] = useState(true);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpReason, setFollowUpReason] = useState('check_up');
  
  // Global search states
  const [globalSearchMode, setGlobalSearchMode] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  
  // Tab search states (for backend-powered search)
  const [tabSearchResults, setTabSearchResults] = useState(null);
  const [tabSearchLoading, setTabSearchLoading] = useState(false);

  // ============ DATA FETCHING ============
  const fetchProducts = async () => {
    try {
      // Load limited initial products - search will use backend API
      const response = await axios.get(`${API_URL}/products?limit=200`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplements = async () => {
    try {
      const [supplementsRes, complementsRes] = await Promise.all([
        axios.get(`${API_URL}/supplements`),
        axios.get(`${API_URL}/products?category=complement&limit=1000`)
      ]);
      const complementProducts = (complementsRes.data || []).map(p => ({
        ...p, item_id: p.id, sub_categories: ['complement'], from_products: true
      }));
      setSupplements([...(supplementsRes.data || []), ...complementProducts]);
    } catch (error) {
      console.error('Failed to fetch supplements:', error);
    }
  };

  const fetchConsumables = async () => {
    try {
      const [consommablesRes, autreRes] = await Promise.all([
        axios.get(`${API_URL}/products?category=consommables&limit=200`),
        axios.get(`${API_URL}/products?category=autre&limit=200`)
      ]);
      setConsumables([...(consommablesRes.data || []), ...(autreRes.data || [])]);
    } catch (error) {
      console.error('Failed to fetch consumables:', error);
    }
  };

  const fetchRecommendedSupplements = useCallback(async () => {
    if (!diagnosis?.id) return;
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(
        `${API_URL}/supplements/recommended/${diagnosis.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecommendedSupplements(response.data.recommended_supplements || []);
    } catch (error) {
      console.error('Failed to fetch recommended supplements:', error);
    }
  }, [diagnosis?.id]);

  const fetchAllRecommendations = useCallback(async () => {
    if (!diagnosis?.id) return;
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(
        `${API_URL}/recommendations/all/${diagnosis.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllRecommendations({
        cosmetics: response.data.cosmetics || [],
        complements: response.data.complements || [],
        spa: response.data.spa || [],
        consumables: response.data.consumables || []
      });
    } catch (error) {
      console.error('Failed to fetch all recommendations:', error);
    }
  }, [diagnosis?.id]);

  // Initial data loading
  useEffect(() => {
    fetchProducts();
    fetchSupplements();
    fetchConsumables();
  }, []);

  // Load recommendations when diagnosis changes
  useEffect(() => {
    if (diagnosis?.id) {
      fetchRecommendedSupplements();
      fetchAllRecommendations();
    }
  }, [diagnosis?.id, fetchRecommendedSupplements, fetchAllRecommendations]);

  // Load products from baby diagnosis into cart
  useEffect(() => {
    if (diagnosis?.products && diagnosis.products.length > 0 && cart.length === 0) {
      // This is likely a baby diagnosis with pre-selected products
      const initialCart = diagnosis.products.map(product => ({
        product_id: product.id || product.code || product.name,
        name: product.name,
        price: product.price || 0,
        quantity: 1,
        routine_step: product.routine_step || null,
        for_concern: product.for_concern || null
      }));
      setCart(initialCart);
    }
  }, [diagnosis?.products]);

  // ============ GLOBAL SEARCH ============
  // ============ GLOBAL SEARCH (using backend API) ============
  const performGlobalSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setGlobalSearchResults([]);
      return;
    }
    setGlobalSearchLoading(true);
    try {
      // Call backend search API instead of filtering in-memory
      const response = await axios.get(`${API_URL}/products/search?q=${encodeURIComponent(query)}&limit=30`);
      const searchResults = response.data || [];
      
      // Categorize results for display
      const results = searchResults.map(p => {
        let resultCategory = 'cosmetics';
        let categoryLabel = language === 'fr' ? 'Cosmétique' : 'Cosmetic';
        
        if (p.category === 'complement' || p.sub_categories?.includes('complement')) {
          resultCategory = 'supplements';
          categoryLabel = language === 'fr' ? 'Complément' : 'Supplement';
        } else if (p.category === 'consommables' || p.category === 'autre') {
          resultCategory = 'consumables';
          categoryLabel = language === 'fr' ? 'Divers' : 'Other';
        }
        
        return { ...p, resultCategory, categoryLabel };
      });
      
      setGlobalSearchResults(results);
    } catch (error) {
      console.error('Global search error:', error);
      setGlobalSearchResults([]);
    } finally {
      setGlobalSearchLoading(false);
    }
  }, [language]);

  useEffect(() => {
    if (globalSearchMode && globalSearchQuery) {
      const timer = setTimeout(() => performGlobalSearch(globalSearchQuery), 300);
      return () => clearTimeout(timer);
    } else {
      setGlobalSearchResults([]);
    }
  }, [globalSearchQuery, globalSearchMode, performGlobalSearch]);

  const addFromGlobalSearch = (item) => {
    if (item.resultCategory === 'cosmetics') { addToCart(item); toast.success(`${item.name} ajouté aux cosmétiques`); }
    else if (item.resultCategory === 'supplements') { addToSupplementCart(item); toast.success(`${item.name} ajouté aux compléments`); }
    else if (item.resultCategory === 'consumables') { addToConsumablesCart(item); toast.success(`${item.name} ajouté aux divers`); }
  };

  // ============ TAB SEARCH (using backend API) ============
  const performTabSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setTabSearchResults(null);
      return;
    }
    setTabSearchLoading(true);
    try {
      const response = await axios.get(`${API_URL}/products/search?q=${encodeURIComponent(query)}&limit=100`);
      setTabSearchResults(response.data || []);
    } catch (error) {
      console.error('Tab search error:', error);
      setTabSearchResults(null);
    } finally {
      setTabSearchLoading(false);
    }
  }, []);

  // Debounced tab search when searchQuery changes
  useEffect(() => {
    if (searchQuery && searchQuery.length >= 2) {
      const timer = setTimeout(() => performTabSearch(searchQuery), 300);
      return () => clearTimeout(timer);
    } else {
      setTabSearchResults(null);
    }
  }, [searchQuery, performTabSearch]);

  // ============ CART OPERATIONS ============
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item => item.product_id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { product_id: product.id, name: product.name, price: product.price || 0, emplacement: product.emplacement, quantity: 1, type: 'product' }];
    });
    toast.success(`${product.name} ${language === 'fr' ? 'ajouté' : 'added'}`);
  };

  const addToSupplementCart = (supplement) => {
    const itemId = supplement.item_id || supplement.id;
    setSupplementCart(prev => {
      const existing = prev.find(item => item.item_id === itemId);
      if (existing) {
        return prev.map(item => item.item_id === itemId ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { item_id: itemId, name: supplement.name, price: supplement.price || 0, form: supplement.form, cure_duration_days: supplement.cure_duration_days, quantity: 1, type: supplement.from_products ? 'complement_product' : 'supplement' }];
    });
    toast.success(`${supplement.name} ${language === 'fr' ? 'ajouté' : 'added'}`);
  };

  const addToConsumablesCart = (product) => {
    setConsumablesCart(prev => {
      const existing = prev.find(item => item.item_id === product.id);
      if (existing) {
        return prev.map(item => item.item_id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { item_id: product.id, name: product.name, price: product.price || 0, quantity: 1, type: 'consumable' }];
    });
    toast.success(`${product.name} ${language === 'fr' ? 'ajouté' : 'added'}`);
  };

  const removeFromCart = (productId) => setCart(prev => prev.filter(item => item.product_id !== productId));
  const removeSupplementFromCart = (itemId) => setSupplementCart(prev => prev.filter(item => item.item_id !== itemId));
  const removeConsumableFromCart = (itemId) => setConsumablesCart(prev => prev.filter(item => item.item_id !== itemId));

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => item.product_id === productId ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
  };
  const updateSupplementQuantity = (itemId, delta) => {
    setSupplementCart(prev => prev.map(item => item.item_id === itemId ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
  };
  const updateConsumableQuantity = (itemId, delta) => {
    setConsumablesCart(prev => prev.map(item => item.item_id === itemId ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
  };

  // ============ CALCULATED VALUES ============
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const supplementCartTotal = supplementCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const consumablesCartTotal = consumablesCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const grandTotal = cartTotal + supplementCartTotal + consumablesCartTotal;
  const cartItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0) + 
                        supplementCart.reduce((sum, item) => sum + item.quantity, 0) +
                        consumablesCart.reduce((sum, item) => sum + item.quantity, 0);

  // ============ FILTERED PRODUCTS ============
  // Use backend search results when available, otherwise filter locally for category/concern only
  const getFilteredProducts = () => {
    // If we have backend search results, use them
    if (tabSearchResults !== null && searchQuery.length >= 2) {
      let results = tabSearchResults;
      // Apply category and concern filters on backend results
      if (selectedCategory !== 'all') {
        results = results.filter(p => p.category === selectedCategory);
      }
      if (selectedConcern !== 'all') {
        results = results.filter(p => productMatchesConcern(p, selectedConcern));
      }
      return results;
    }
    
    // No search query - filter from limited initial load or show empty until searched
    // For better UX, show initial products when no search is active
    if (!searchQuery || searchQuery.length < 2) {
      return products.filter(product => {
        const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
        const matchesConcern = productMatchesConcern(product, selectedConcern);
        return matchesCategory && matchesConcern;
      });
    }
    
    // Fallback to local filter if backend fails
    return products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           product.main_ingredient?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      const matchesConcern = productMatchesConcern(product, selectedConcern);
      return matchesSearch && matchesCategory && matchesConcern;
    });
  };

  const filteredProducts = getFilteredProducts();

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const aAvailable = a.is_available !== false ? 1 : 0;
    const bAvailable = b.is_available !== false ? 1 : 0;
    if (aAvailable !== bAvailable) return bAvailable - aAvailable;
    if (diagnosis?.main_concern) {
      const aMatches = a.target_concerns?.includes(diagnosis.main_concern) ? 1 : 0;
      const bMatches = b.target_concerns?.includes(diagnosis.main_concern) ? 1 : 0;
      if (aMatches !== bMatches) return bMatches - aMatches;
    }
    return (a.name || '').localeCompare(b.name || '');
  });

  // ============ SALE HANDLING ============
  const handleValidateSale = async () => {
    if (saleCompleted || submitting) {
      toast.error(language === 'fr' ? 'Vente déjà enregistrée' : 'Sale already recorded');
      return;
    }
    if (!ticketMediciel || !ticketMediciel.trim()) {
      toast.error(language === 'fr' ? 'Le numéro de ticket Mediciel est obligatoire' : 'Mediciel ticket number is required');
      return;
    }
    if (cart.length === 0 && supplementCart.length === 0 && consumablesCart.length === 0) {
      toast.error(language === 'fr' ? 'Le panier est vide' : 'Cart is empty');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const allProducts = [
        ...cart.map(item => ({ type: 'product', item_id: item.product_id, name: item.name, price: item.price, quantity: item.quantity })),
        ...consumablesCart.map(item => ({ type: 'consumable', item_id: item.item_id, name: item.name, price: item.price, quantity: item.quantity }))
      ];
      
      const response = await axios.post(`${API_URL}/sales/complete`, {
        diagnosis_id: diagnosis?.id,
        client_id: diagnosis?.client_id,
        client_name: diagnosis?.client_name || 'Client',
        products: allProducts,
        supplements: supplementCart.map(item => ({ type: 'supplement', item_id: item.item_id, name: item.name, price: item.price, quantity: item.quantity })),
        schedule_renewals: scheduleRenewals,
        ticket_mediciel: ticketMediciel.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (followUpDate && diagnosis?.client_id) {
        try {
          await axios.post(`${API_URL}/clients/${diagnosis.client_id}/follow-up`, {
            follow_up_date: followUpDate, follow_up_reason: followUpReason,
            notes: `Suite à vente du ${new Date().toLocaleDateString('fr-FR')}`
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (err) { console.error('Failed to schedule follow-up:', err); }
      }

      const allItems = [...cart.map(item => ({ ...item, type: 'product' })), ...consumablesCart.map(item => ({ ...item, type: 'consumable' })), ...supplementCart.map(item => ({ ...item, type: 'supplement' }))];
      const receipt = {
        sale_id: response.data.sale_id, date: new Date().toLocaleString('fr-FR'),
        client: diagnosis?.client_name || 'Client', client_phone: diagnosis?.client_phone,
        items: allItems, products: cart, consumables: consumablesCart, supplements: supplementCart,
        products_total: cartTotal, consumables_total: consumablesCartTotal, supplements_total: supplementCartTotal,
        total: grandTotal, renewal_dates: response.data.renewal_dates || [], follow_up_date: followUpDate, ticket_mediciel: ticketMediciel.trim()
      };
      
      setSaleReceipt(receipt);
      setSaleCompleted(true);
      setShowConfirmation(true);
      toast.success(language === 'fr' ? 'Vente enregistrée!' : 'Sale recorded!');
      
      setCureSetupData({
        client_name: diagnosis?.client_name || 'Client', client_phone: diagnosis?.client_phone, client_email: diagnosis?.client_email,
        sale_id: response.data.sale_id, ticket_mediciel: ticketMediciel.trim(), products: allProducts,
        supplements: supplementCart.map(item => ({ type: 'supplement', item_id: item.item_id, name: item.name, price: item.price, quantity: item.quantity }))
      });
      setTimeout(() => setShowCureSetup(true), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!saleReceipt?.sale_id) return;
    try {
      const response = await axios.get(`${API_URL}/sales/${saleReceipt.sale_id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SADASkinCare_Vente_${saleReceipt.sale_id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'PDF téléchargé!' : 'PDF downloaded!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de téléchargement' : 'Download failed');
    }
  };

  const handleFinish = () => {
    setShowConfirmation(false);
    setCart([]);
    setSupplementCart([]);
    setConsumablesCart([]);
    setSaleCompleted(false);
    setSaleReceipt(null);
    setTicketMediciel('');
    if (onSaleComplete) onSaleComplete();
    else onBack();
  };

  // ============ RENDER ============
  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-28 sm:pb-32">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E7E5E4] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-[#78716C] hover:text-[#0F172A] transition-colors" data-testid="products-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-[#0F172A]">
                {language === 'fr' ? 'Produits & Compléments' : 'Products & Supplements'}
              </h1>
              {diagnosis?.client_name && <p className="text-sm text-[#78716C]">{diagnosis.client_name}</p>}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {(diagnosis?.client_id || diagnosis?.client_phone) && (
              <Button onClick={() => setShowLoyalty(true)} variant="outline" className="border-amber-400 text-amber-600 hover:bg-amber-50 rounded-full" data-testid="loyalty-btn">
                <Gift className="w-5 h-5 sm:mr-2" />
                <span className="hidden sm:inline">{language === 'fr' ? 'Fidélité' : 'Loyalty'}</span>
              </Button>
            )}
            <Button onClick={() => setShowCart(true)} className="relative bg-[#0F172A] hover:bg-[#334155] text-white rounded-full" data-testid="view-cart-btn">
              <ShoppingCart className="w-5 h-5 mr-2" />
              {grandTotal.toLocaleString('fr-FR')} FCFA
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-[#2E7D32] rounded-full text-xs flex items-center justify-center">{cartItemsCount}</span>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Global Search */}
        <GlobalSearchSection
          globalSearchMode={globalSearchMode}
          setGlobalSearchMode={setGlobalSearchMode}
          globalSearchQuery={globalSearchQuery}
          setGlobalSearchQuery={setGlobalSearchQuery}
          globalSearchResults={globalSearchResults}
          globalSearchLoading={globalSearchLoading}
          addFromGlobalSearch={addFromGlobalSearch}
          language={language}
        />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="cosmetics" className="flex items-center gap-2" data-testid="tab-cosmetics">
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'fr' ? 'Cosmétiques' : 'Cosmetics'}</span>
              <span className="sm:hidden">Cosm.</span>
              {cart.length > 0 && <Badge className="bg-[#2E7D32] text-white text-xs">{cart.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="supplements" className="flex items-center gap-2" data-testid="tab-supplements">
              <Pill className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'fr' ? 'Compléments' : 'Supplements'}</span>
              <span className="sm:hidden">Compl.</span>
              {supplementCart.length > 0 && <Badge className="bg-green-500 text-white text-xs">{supplementCart.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="spa" className="flex items-center gap-2" data-testid="tab-spa">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'fr' ? 'Spa' : 'Spa'}</span>
              <span className="sm:hidden">Spa</span>
            </TabsTrigger>
            <TabsTrigger value="consumables" className="flex items-center gap-2" data-testid="tab-consumables">
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'fr' ? 'Divers' : 'Misc'}</span>
              <span className="sm:hidden">Div.</span>
              {consumablesCart.length > 0 && <Badge className="bg-orange-500 text-white text-xs">{consumablesCart.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cosmetics">
            <CosmeticsTab
              products={sortedProducts}
              allRecommendations={allRecommendations}
              loading={loading || tabSearchLoading}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedConcern={selectedConcern}
              setSelectedConcern={setSelectedConcern}
              cart={cart}
              addToCart={addToCart}
              updateQuantity={updateQuantity}
              diagnosis={diagnosis}
              language={language}
            />
          </TabsContent>

          <TabsContent value="supplements">
            <SupplementsTab
              supplements={supplements}
              allRecommendations={allRecommendations}
              recommendedSupplements={recommendedSupplements}
              supplementSearch={supplementSearch}
              setSupplementSearch={setSupplementSearch}
              selectedSupplementCategory={selectedSupplementCategory}
              setSelectedSupplementCategory={setSelectedSupplementCategory}
              selectedConcern={selectedConcern}
              supplementCart={supplementCart}
              addSupplementToCart={addToSupplementCart}
              updateSupplementQuantity={updateSupplementQuantity}
              addToCart={addToCart}
              language={language}
            />
          </TabsContent>

          <TabsContent value="spa">
            <SpaTab products={products} allRecommendations={allRecommendations} addToCart={addToCart} language={language} />
          </TabsContent>

          <TabsContent value="consumables">
            <ConsumablesTab
              consumables={consumables}
              allRecommendations={allRecommendations}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              consumablesCart={consumablesCart}
              addToConsumablesCart={addToConsumablesCart}
              updateConsumableQuantity={updateConsumableQuantity}
              addToCart={addToCart}
              language={language}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Bottom Sale Bar */}
      <BottomSaleBar
        cart={cart}
        supplementCart={supplementCart}
        consumablesCart={consumablesCart}
        cartTotal={cartTotal}
        supplementCartTotal={supplementCartTotal}
        grandTotal={grandTotal}
        ticketMediciel={ticketMediciel}
        setTicketMediciel={setTicketMediciel}
        scheduleRenewals={scheduleRenewals}
        setScheduleRenewals={setScheduleRenewals}
        followUpDate={followUpDate}
        setFollowUpDate={setFollowUpDate}
        followUpReason={followUpReason}
        setFollowUpReason={setFollowUpReason}
        handleValidateSale={handleValidateSale}
        submitting={submitting}
        language={language}
      />

      {/* Cart Dialog */}
      <CartDialog
        open={showCart}
        onOpenChange={setShowCart}
        cart={cart}
        supplementCart={supplementCart}
        consumablesCart={consumablesCart}
        cartTotal={cartTotal}
        supplementCartTotal={supplementCartTotal}
        consumablesCartTotal={consumablesCartTotal}
        grandTotal={grandTotal}
        ticketMediciel={ticketMediciel}
        setTicketMediciel={setTicketMediciel}
        updateQuantity={updateQuantity}
        updateSupplementQuantity={updateSupplementQuantity}
        updateConsumableQuantity={updateConsumableQuantity}
        removeFromCart={removeFromCart}
        removeSupplementFromCart={removeSupplementFromCart}
        removeConsumableFromCart={removeConsumableFromCart}
        handleValidateSale={handleValidateSale}
        submitting={submitting}
        language={language}
      />

      {/* Sale Confirmation Dialog */}
      <SaleConfirmationDialog
        open={showConfirmation}
        onOpenChange={setShowConfirmation}
        saleReceipt={saleReceipt}
        handleDownloadPDF={handleDownloadPDF}
        handleFinish={handleFinish}
        language={language}
      />

      {/* Loyalty Dialog */}
      <Dialog open={showLoyalty} onOpenChange={setShowLoyalty}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-600" />
              {language === 'fr' ? 'Programme Fidélité' : 'Loyalty Program'}
            </DialogTitle>
          </DialogHeader>
          <LoyaltyCard clientId={diagnosis?.client_id} clientName={diagnosis?.client_name} clientPhone={diagnosis?.client_phone} />
        </DialogContent>
      </Dialog>

      {/* Cure Setup Modal */}
      <CureSetupModal
        open={showCureSetup}
        onClose={(success) => {
          setShowCureSetup(false);
          if (success) toast.success(language === 'fr' ? 'Un rappel sera envoyé 5 jours avant la fin de la cure' : 'A reminder will be sent 5 days before the cure ends');
        }}
        onComplete={() => {
          // Navigate back after cure setup
          if (onSaleComplete) {
            onSaleComplete();
          } else if (onBack) {
            onBack();
          }
        }}
        saleData={cureSetupData}
        language={language}
      />
    </div>
  );
};

export default ProductsPage;
